import { ChevronDown, Plus, Star, Tag, Trash2 } from 'lucide-react';
import { PROMO_TAGS } from '../../../../constants/marketing-display.constants';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';

interface Tarif {
  label: string;
  price: number;
  unit: string;
  detail?: string;
  note?: string; // On va utiliser ce champ pour stocker le promo tag
  order: number;
}

interface TarifGroup {
  title: string;
  description?: string;
  tarifs: Tarif[];
}

interface TarifMarketingEditorProps {
  groups: TarifGroup[];
  onChange: (groups: TarifGroup[]) => void;
}

export function TarifMarketingEditor({ groups, onChange }: TarifMarketingEditorProps) {
  const addGroup = () => {
    onChange([...groups, { title: 'Tarifs & Durée (Hors Taxes)', tarifs: [] }]);
  };

  const updateGroup = (gi: number, patch: Partial<TarifGroup>) => {
    const next = [...groups];
    next[gi] = { ...next[gi], ...patch };
    onChange(next);
  };

  const removeGroup = (gi: number) => {
    onChange(groups.filter((_, i) => i !== gi));
  };

  const addTarif = (gi: number) => {
    const next = [...groups];
    next[gi].tarifs = [
      ...next[gi].tarifs,
      { label: 'Nouvelle offre', price: 0, unit: 'FCFA', order: next[gi].tarifs.length + 1 },
    ];
    onChange(next);
  };

  const updateTarif = (gi: number, ti: number, patch: Partial<Tarif>) => {
    const next = [...groups];
    next[gi].tarifs[ti] = { ...next[gi].tarifs[ti], ...patch };
    onChange(next);
  };

  const removeTarif = (gi: number, ti: number) => {
    const next = [...groups];
    next[gi].tarifs = next[gi].tarifs.filter((_, i) => i !== ti);
    onChange(next);
  };

  return (
    <div className="space-y-10">
      {groups.map((group, gi) => (
        <div
          key={gi}
          className="bg-white border-2 border-gray-100 rounded-[40px] p-10 space-y-8 shadow-sm hover:shadow-md transition-all relative overflow-hidden"
        >
          {/* Header de groupe */}
          <div className="flex items-start justify-between gap-6 border-b border-gray-50 pb-6">
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2 mb-1">
                <Tag size={14} className="text-[#00758d]" />
                <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                  Section de tarification
                </Label>
              </div>
              <Input
                value={group.title}
                onChange={(e) => updateGroup(gi, { title: e.target.value })}
                className="font-black text-[#00303c] border-none px-0 h-auto focus-visible:ring-0 shadow-none text-2xl"
                placeholder="Ex: Tarifs & Durée"
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              onClick={() => removeGroup(gi)}
              className="w-12 h-12 rounded-2xl text-red-300 hover:text-red-600 hover:bg-red-50 transition-all"
            >
              <Trash2 size={20} />
            </Button>
          </div>

          {/* Liste des tarifs */}
          <div className="grid grid-cols-1 gap-6">
            {group.tarifs.map((tarif, ti) => {
              const promoTag = PROMO_TAGS.find((t) => t.value === tarif.note);
              const isBestValue = tarif.note === 'BEST VALUE';

              return (
                <div
                  key={ti}
                  className={`grid grid-cols-12 gap-6 items-center p-8 rounded-[32px] border-2 transition-all relative group
                    ${
                      isBestValue
                        ? 'border-[#00758d] bg-[#00758d]/5 shadow-2xl shadow-[#00758d]/10'
                        : promoTag?.value
                          ? 'border-amber-100 bg-amber-50/20'
                          : 'border-gray-50 bg-gray-50/30 hover:border-gray-200 hover:bg-white'
                    }`}
                >
                  {/* Visual Marker for Best Value */}
                  {isBestValue && (
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-4 py-1.5 bg-[#00758d] text-white rounded-full flex items-center gap-2 shadow-lg animate-bounce duration-1000">
                      <Star size={12} fill="white" />
                      <span className="text-[10px] font-black uppercase tracking-widest">Offre recommandée</span>
                    </div>
                  )}

                  {/* Libellé */}
                  <div className="col-span-4 space-y-2">
                    <Label className="text-[9px] font-black uppercase tracking-widest text-gray-400 block ml-1">
                      Désignation
                    </Label>
                    <Input
                      value={tarif.label}
                      onChange={(e) => updateTarif(gi, ti, { label: e.target.value })}
                      className={`h-12 text-sm font-black bg-white rounded-2xl border-gray-200 focus:border-[#00758d] focus:ring-4 focus:ring-[#00758d]/5 transition-all shadow-none ${isBestValue ? 'text-[#00758d]' : 'text-[#00303c]'}`}
                    />
                  </div>

                  {/* Prix */}
                  <div className="col-span-3 space-y-2">
                    <Label className="text-[9px] font-black uppercase tracking-widest text-gray-400 block ml-1">
                      Tarif HT
                    </Label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={tarif.price}
                        onChange={(e) => updateTarif(gi, ti, { price: parseInt(e.target.value) || 0 })}
                        className="h-12 text-lg font-black bg-white rounded-2xl border-gray-200 focus:border-[#00758d] focus:ring-4 focus:ring-[#00758d]/5 text-[#00303c] pr-12 shadow-none"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-black text-gray-300">
                        XAF
                      </span>
                    </div>
                  </div>

                  {/* Badge Promo Selector */}
                  <div className="col-span-4 space-y-2">
                    <Label className="text-[9px] font-black uppercase tracking-widest text-gray-400 block ml-1 text-right">
                      Badge Marketing
                    </Label>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <select
                          value={tarif.note || ''}
                          onChange={(e) => updateTarif(gi, ti, { note: e.target.value })}
                          className={`w-full h-12 pl-4 pr-10 bg-white border-2 rounded-2xl text-xs font-black outline-none transition-all appearance-none cursor-pointer
                            ${promoTag?.value ? 'border-[#00758d]/30 text-[#00758d]' : 'border-gray-200 text-gray-400 hover:border-gray-300'}`}
                        >
                          {PROMO_TAGS.map((tag) => (
                            <option key={tag.value} value={tag.value}>
                              {tag.label}
                            </option>
                          ))}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-300">
                          <ChevronDown size={16} />
                        </div>
                      </div>

                      {/* Live Visual Preview of the tag */}
                      {promoTag?.value && (
                        <div
                          className="px-3 py-2 rounded-xl text-[10px] font-black text-white uppercase shadow-sm animate-in zoom-in-50 duration-300 whitespace-nowrap"
                          style={{ background: promoTag.color }}
                        >
                          {promoTag.value}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Suppression */}
                  <div className="col-span-1 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => removeTarif(gi, ti)}
                      className="w-10 h-10 rounded-xl text-red-200 hover:text-red-500 hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>
              );
            })}

            <Button
              type="button"
              onClick={() => addTarif(gi)}
              variant="ghost"
              className="w-full mt-4 text-[#00758d] font-black uppercase tracking-widest text-[10px] bg-[#00758d]/5 hover:bg-[#00758d]/10 border-none h-16 rounded-[24px] transition-all"
            >
              <Plus size={20} className="mr-2" /> Ajouter une offre de prix
            </Button>
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        onClick={addGroup}
        className="w-full border-dashed border-4 border-gray-100 text-gray-300 py-16 rounded-[50px] hover:bg-gray-50 hover:border-[#00758d]/20 hover:text-[#00758d] transition-all group"
      >
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-[#00758d]/10 transition-colors shadow-sm">
            <Plus size={32} className="group-hover:scale-110 transition-all" />
          </div>
          <span className="block font-black uppercase tracking-[0.3em] text-[12px]">
            Ajouter une section tarifaire complète
          </span>
        </div>
      </Button>
    </div>
  );
}
